import { useEffect, useState } from 'react';
import EditorLayout from '../../components/Layout/EditorLayout';
import { api } from '../../services/api';

interface PublicationEntry {
  id: string;
  companyName: string;
  applicationType: string;
  usePreviousPublication: boolean;
}

export default function EditorPublicationPage() {
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [entries, setEntries]     = useState<PublicationEntry[]>([]);

  useEffect(() => {
    api.editor.getPublicationList()
      .then((res) => setEntries(res.applications))
      .catch(() => setListError('A lista betöltése sikertelen.'))
      .finally(() => setListLoading(false));
  }, []);

  async function handleDownload() {
    setLoading(true);
    setError('');
    try {
      const res = await api.editor.downloadPublicationZip();
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError((body as { message?: string }).message ?? 'Letöltés sikertelen.');
        return;
      }
      const blob  = await res.blob();
      const url   = URL.createObjectURL(blob);
      const a     = document.createElement('a');
      a.href      = url;
      const cd    = res.headers.get('Content-Disposition') ?? '';
      const match = /filename="([^"]+)"/.exec(cd);
      a.download  = match?.[1] ?? 'kiadvany_anyagok.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setError('Hálózati hiba. Kérjük, próbálja újra.');
    } finally {
      setLoading(false);
    }
  }

  const newUploads  = entries.filter((e) => !e.usePreviousPublication);
  const usePrevious = entries.filter((e) =>  e.usePreviousPublication);

  const th: React.CSSProperties = { padding: '8px 12px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: 'var(--secondary-foreground)', textTransform: 'uppercase', letterSpacing: '.06em', borderBottom: '1px solid var(--border)' };
  const td: React.CSSProperties = { padding: '9px 12px', fontSize: 13, color: 'var(--primary)', borderBottom: '1px solid var(--border)' };

  return (
    <EditorLayout>
      <div style={{ padding: '40px 48px', maxWidth: 760 }}>
        <h1 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700, color: 'var(--primary)' }}>
          Kiadvány anyagok letöltése
        </h1>
        <p style={{ fontSize: 14, color: 'var(--muted-foreground)', marginBottom: 32 }}>
          Az elfogadott pályázók listája és kiadványanyag-választásuk. A ZIP csak az új feltöltéseket tartalmazza.
        </p>

        {error && (
          <div className="alert alert-destructive" style={{ marginBottom: 20 }}>
            <span>&#9888;</span>
            <div><p>{error}</p></div>
          </div>
        )}

        <button
          className="btn btn-primary"
          onClick={() => void handleDownload()}
          disabled={loading || entries.length === 0}
          style={{ fontSize: 14, padding: '10px 24px', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 40 }}
        >
          {loading ? (
            <>
              <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
              Letöltés folyamatban...
            </>
          ) : (
            <>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Kiadvány anyagok letöltése (.zip)
            </>
          )}
        </button>

        {listLoading && (
          <div style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>Lista betöltése...</div>
        )}
        {listError && (
          <div className="alert alert-destructive"><span>&#9888;</span><div><p>{listError}</p></div></div>
        )}

        {!listLoading && !listError && entries.length === 0 && (
          <div style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>Nincs elfogadott pályázat.</div>
        )}

        {newUploads.length > 0 && (
          <div style={{ marginBottom: 36 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)', marginBottom: 10 }}>
              Új anyagot töltöttek fel ({newUploads.length} pályázó)
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <thead>
                <tr style={{ background: 'var(--muted)' }}>
                  <th style={th}>Cég neve</th>
                  <th style={th}>Típus</th>
                </tr>
              </thead>
              <tbody>
                {newUploads.map((e) => (
                  <tr key={e.id}>
                    <td style={td}>{e.companyName}</td>
                    <td style={td}>{e.applicationType === 'renewal' ? 'Megújítói' : 'Új'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {usePrevious.length > 0 && (
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)', marginBottom: 10 }}>
              Tavalyi adatok felhasználását kérték ({usePrevious.length} pályázó) — manuális előkeresés szükséges
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <thead>
                <tr style={{ background: 'var(--muted)' }}>
                  <th style={th}>Cég neve</th>
                  <th style={th}>Típus</th>
                </tr>
              </thead>
              <tbody>
                {usePrevious.map((e) => (
                  <tr key={e.id}>
                    <td style={td}>{e.companyName}</td>
                    <td style={td}>{e.applicationType === 'renewal' ? 'Megújítói' : 'Új'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </EditorLayout>
  );
}
